import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";

import { useNavigate, useSearchParams } from "react-router-dom";
import { getVerifyHostedPageUrl, getVerifyPurchaseWithTransactionId } from "api/hiredrateURLConstruct";
import { normalizeAmount } from "utils/helperUtil";
import AppConfig from "api/AppConfig";

const AddonSuccess = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const configObject = AppConfig("APIConfig");

  const hostedPageId = searchParams.get("id");
  const paymentState = searchParams.get("state");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Chargebee response from verify-hosted-page (normalized structure)
  const [transactionDetails, setTransactionDetails] = useState(null);
  
  // Payment verification data from verify-payment endpoint
  const [paymentVerification, setPaymentVerification] = useState(null);

  // UI states
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);

  /**
   * --------------------------
   * FORMATTERS
   * --------------------------
   */

  const formatCurrency = (amount, currency = "INR") => {
    const normalized = normalizeAmount(amount);

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(normalized);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toString().length === 10 
      ? new Date(timestamp * 1000) 
      : new Date(timestamp);
    return date.toLocaleString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (paymentState !== "succeeded") {
      setError("Payment was not successful.");
      setLoading(false);
      return;
    }

    if (!hostedPageId) {
      setError("Hosted page ID not found.");
      setLoading(false);
      return;
    }

    fetchTransactionDetails();
  }, [hostedPageId, paymentState]);

  // Step 1: Get transaction details using /verify-hosted-page endpoint (normalized response)
  const fetchTransactionDetails = async (attempt = 1, maxAttempts = 3) => {
    try {
      setLoading(true);

      const response = await fetch(getVerifyHostedPageUrl(), {
        method: "POST",
        ...configObject,
        headers: {
          ...configObject?.headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ hosted_page_id: hostedPageId }),
      });

      const data = await response.json();

      if (data.status === "success") {
        // The data is already in normalized structure
        const responseData = data.data;
        // Extract transaction info from normalized structure
        const transactionInfo = {
          transaction_id: responseData?.transaction?.payment?.transaction_id,
          hosted_page_id: responseData?.transaction?.hosted_page_id,
          invoice_id: responseData?.transaction?.invoice_id,
          subscription_id: responseData?.transaction?.subscription_id,
          customer_email: responseData?.transaction?.customer_email,
          currency: responseData?.transaction?.currency,
          payment_status: responseData?.transaction?.payment_status,
          total_amount: responseData?.transaction?.invoice?.total,
          transaction_date: responseData?.transaction?.payment?.transaction_date,
          event_type: responseData?.event_type,
          event_subtype: responseData?.event_subtype,
          items: responseData?.resource?.items || [],
          summary: responseData?.resource?.summary || {},
        };

        setTransactionDetails(transactionInfo);

        // Step 2: Verify payment using verify-payment endpoint
        const transactionId = transactionInfo.transaction_id;
        if (transactionId) {
          await verifyPayment(transactionId);
        } else {
          setError("No transaction ID found");
        }
        setLoading(false);
        return;
      }

      // Not ready yet — retry instead of failing immediately
      if (attempt < maxAttempts) {
        const delay = Math.min(1500 * attempt, 6000); // 1.5s, 3s, 4.5s, 6s...
        setTimeout(() => fetchTransactionDetails(attempt + 1, maxAttempts), delay);
        return;
      }

      setError(data.message || "Unable to fetch payment details.");
      setLoading(false);
    } catch (err) {
      if (attempt < maxAttempts) {
        const delay = Math.min(1500 * attempt, 6000);
        setTimeout(() => fetchTransactionDetails(attempt + 1, maxAttempts), delay);
        return;
      }
      setError(err.message || "Something went wrong.");
      setLoading(false);
    }
  };

  // Step 2: Verify payment using /verify-payment endpoint
  const verifyPayment = async (transactionId, isManualCheck = false) => {
    setCheckingPayment(true);

    try {
      const response = await fetch(
        getVerifyPurchaseWithTransactionId(transactionId),
        {
          method: "GET",
           ...configObject,
          headers: {
             ...configObject?.headers,
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      const paymentData =
        result?.sso_response?.sso_response?.data || [];

      if (paymentData.length > 0) {
        const payment = paymentData[0];

        setPaymentVerification(payment);
        setPaymentVerified(true);


        return true;
      }

      setPaymentVerification(null);
      setPaymentVerified(false);


      return false;
    } catch (err) {
      console.error("Error verifying payment:", err);

      setPaymentVerification(null);
      setPaymentVerified(false);

      return false;
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleManualCheck = async () => {
    const transactionId = transactionDetails?.transaction_id;

    if (!transactionId) return;

    await verifyPayment(transactionId, true);
  };

  /**
   * --------------------------
   * LOADING SCREEN
   * --------------------------
   */

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.secondaryLight || '#f5f3ff'} 50%, ${theme.palette.infoLight || '#ecfeff'} 100%)`,
        }}
      >
        <Stack alignItems="center" spacing={3}>
          <CircularProgress size={55} thickness={4} />
          <Typography fontWeight={600}>
            Verifying your payment...
          </Typography>
        </Stack>
      </Box>
    );
  }

  /**
   * --------------------------
   * ERROR SCREEN
   * --------------------------
   */

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.secondaryLight || '#f5f3ff'} 50%, ${theme.palette.infoLight || '#ecfeff'} 100%)`,
        }}
      >
        <Card
          sx={{
            maxWidth: 420,
            width: "100%",
            borderRadius: 5,
            p: 3,
          }}
        >
          <Alert severity="error">{error}</Alert>

          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 3, py: 1.4, borderRadius: 3 }}
            onClick={() => navigate("/settings/subscription")}
          >
            Close
          </Button>
        </Card>
      </Box>
    );
  }

  const data = transactionDetails;
  const items = data?.items || [];
  const currency = data?.currency || "INR";
  const isAddon = data?.event_type === "addon";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 6,
        px: 2,
        background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.secondaryLight || '#f5f3ff'} 50%, ${theme.palette.infoLight || '#ecfeff'} 100%)`,
      }}
    >
      <Fade in timeout={600}>
        <Card
          elevation={0}
          sx={{
            maxWidth: 760,
            mx: "auto",
            borderRadius: 8,
            overflow: "hidden",
            border: `1px solid ${theme.palette.common.white}`,
            backdropFilter: "blur(20px)",
            background: "rgba(255,255,255,0.88)",
            boxShadow:
              "0 20px 80px rgba(15, 23, 42, 0.08)",
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              background: `linear-gradient(135deg, ${theme.palette.grey[900]} 0%, ${theme.palette.grey[700]} 100%)`,
              color: "white",
              px: 4,
              py: 5,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2.5} alignItems="center">
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "22px",
                    bgcolor: "rgba(255,255,255,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 44, color: theme.palette.success.main }}
                  />
                </Box>

                <Box>
                  <Typography variant="h4" fontWeight={800}>
                    Payment Successful
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.8, opacity: 0.8 }}>
                    Your payment has been received successfully.
                  </Typography>
                </Box>
              </Stack>

              <Chip
                icon={<VerifiedRoundedIcon />}
                label="PAYMENT CONFIRMED"
                sx={{
                  bgcolor: theme.palette.success.dark,
                  color: "white",
                  fontWeight: 700,
                  px: 1,
                }}
              />
            </Stack>
          </Box>

          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            {/* PAYMENT RECEIVED */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 5,
                bgcolor: theme.palette.grey[50],
                border: `1px solid ${theme.palette.grey[200]}`,
                mb: 3,
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <ReceiptLongRoundedIcon color="primary" />
                    <Typography fontWeight={700}>Payment Receipt</Typography>
                  </Stack>
                  <Typography variant="h5" fontWeight={800} color="primary.main">
                    {formatCurrency(data?.total_amount, currency)}
                  </Typography>
                </Stack>

                <Divider />

                <Stack spacing={1}>
                  <InfoRow label="Invoice ID" value={data?.invoice_id} mono />
                  <InfoRow label="Transaction ID" value={data?.transaction_id} mono />
                  <InfoRow label="Payment Date" value={formatDate(data?.transaction_date)} />
                </Stack>
              </Stack>
            </Paper>

            {/* PURCHASED ADDONS / ITEMS */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 5,
                border: `1px solid ${theme.palette.grey[200]}`,
                mb: 3,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                <Inventory2RoundedIcon color="primary" />
                <Typography fontWeight={700}>Purchased Items</Typography>
              </Stack>

              {items.length > 0 ? (
                <List disablePadding>
                  {items.map((item, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <Divider />}
                      <ListItem sx={{ px: 0, py: 2 }}>
                        
                        <ListItemText
                          primary={<Typography fontWeight={700}>{item?.name}</Typography>}
                          secondary={
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              <Chip label={`Qty ${item?.quantity || 1}`} size="small" />
                              <Chip label={item?.pricing_model || "flat_fee"} size="small" variant="outlined" />
                            </Stack>
                          }
                        />
                        <Typography fontWeight={800} color="primary.main">
                          {formatCurrency(item?.amount, currency)}
                        </Typography>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No items found in this transaction.
                </Typography>
              )}
            </Paper>

            {/* PAYMENT VERIFICATION STATUS */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 5,
                mb: 3,
                border: paymentVerified 
                  ? `1px solid ${theme.palette.success.light}` 
                  : `1px solid ${theme.palette.warning.light}`,
                bgcolor: paymentVerified 
                  ? theme.palette.successLight || '#f0fdf4'
                  : theme.palette.warningLight || '#fffdf3',
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {paymentVerified ? (
                    <TaskAltRoundedIcon sx={{ color: theme.palette.success.main }} />
                  ) : (
                    <HourglassTopRoundedIcon sx={{ color: theme.palette.warning.main }} />
                  )}
                  <Typography fontWeight={700}>
                    {paymentVerified ? "Payment Verified Successfully" : "Verifying Your Payment"}
                  </Typography>
                </Stack>

                {!paymentVerified ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Your payment is confirmed. We are currently verifying your purchase in our system.
                    </Typography>

                    <LinearProgress sx={{ borderRadius: 999, height: 8 }} />

                    <Alert 
                      severity="info" 
                      sx={{ 
                        borderRadius: 3,
                        '& .MuiAlert-icon': {
                          color: theme.palette.info.main
                        }
                      }}
                    >
                      This usually takes a few seconds. If your payment is not verified yet, click the button below to refresh.
                    </Alert>

                    <Button
                      variant="contained"
                      startIcon={checkingPayment ? <CircularProgress size={18} sx={{ color: "white" }} /> : <RefreshRoundedIcon />}
                      disabled={checkingPayment}
                      onClick={handleManualCheck}
                      sx={{ 
                        alignSelf: "flex-start", 
                        borderRadius: 3, 
                        px: 3, 
                        py: 1.2, 
                        textTransform: "none", 
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      }}
                    >
                      {checkingPayment ? "Verifying..." : "Check Payment Status"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Your payment has been fully verified and limits are now available in your account.
                    </Typography>

                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 4, 
                        bgcolor: "white", 
                        border: `1px solid ${theme.palette.successLight || '#dcfce7'}` 
                      }}
                    >
                      <Stack spacing={1.5}>
                        <InfoRow label="Credit Purchase ID" value={paymentVerification?.credit_purchase_id} mono />
                        <InfoRow label="Purchase Status" value={paymentVerification?.purchase_tx_response?.toUpperCase()} />
                        <InfoRow label="Purchase Cost" value={formatCurrency(paymentVerification?.purchase_cost, currency)} icon={<CurrencyRupeeRoundedIcon />} />
                      </Stack>
                    </Paper>
                  </>
                )}
              </Stack>
            </Paper>

            {/* ACTION BUTTONS */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button
                fullWidth
                size="large"
                variant="contained"
                startIcon={<DashboardRoundedIcon />}
                onClick={() => navigate("/settings/subscription")}
                sx={{
                  py: 1.6,
                  borderRadius: 4,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "1rem",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                }}
              >
                Close
              </Button>
            </Stack>

            <Typography variant="caption" display="block" textAlign="center" color="text.secondary" mt={3}>
              A confirmation receipt has been sent to your registered email address.
            </Typography>
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
};

/**
 * --------------------------
 * REUSABLE INFO ROW
 * --------------------------
 */

const InfoRow = ({ label, value, mono = false, icon = null }) => {
  const theme = useTheme();
  
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography 
        variant="body2" 
        fontWeight={700} 
        fontFamily={mono ? "monospace" : "inherit"} 
        textAlign="right"
        color="text.primary"
      >
        {value || "-"}
      </Typography>
    </Stack>
  );
};

export default AddonSuccess;